import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiPlus, FiEdit2, FiPackage, FiX, FiTrash2, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import Swal from 'sweetalert2';
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function InventoryPage() {
    const [summary, setSummary] = useState({
        totalFlavors: 0,
        lowStockAlerts: 0,
        avgDailyUsage: "0%"
    });
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [stocks, setStocks] = useState([]);
    const [basesStock, setBasesStock] = useState([]);
    const [flavors, setFlavors] = useState([]);
    const [availableBases, setAvailableBases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAddBaseModalOpen, setIsAddBaseModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditBaseModalOpen, setIsEditBaseModalOpen] = useState(false);
    const [isEditFlavorModalOpen, setIsEditFlavorModalOpen] = useState(false);
    const [isNewFlavorModalOpen, setIsNewFlavorModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editBaseTarget, setEditBaseTarget] = useState(null);
    const [editFlavorTarget, setEditFlavorTarget] = useState(null);
    const [newInventory, setNewInventory] = useState({ flavor_id: "", stock: 0, price: "", loadCell_id_FK: "" });
    const [newBaseInventory, setNewBaseInventory] = useState({ base_id: "", stock: 0, price: "", loadCell_id_FK: "" });
    const [newFlavor, setNewFlavor] = useState({ name: "", price: "", image: null });
    const [loadCells, setLoadCells] = useState([]);
    const userRole = localStorage.getItem("role");

    const fetchDeviceStock = useCallback(async (deviceId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/machine-flavor-stock/device/${deviceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // The API now returns { status: true, flavors: [...], bases: [...] }
            if (response.data && typeof response.data === 'object' && response.data.flavors) {
                setStocks(response.data.flavors);
                if (response.data.bases) {
                    setBasesStock(response.data.bases);
                }
            } else {
                // Fallback for older API versions or direct array responses
                setStocks(Array.isArray(response.data) ? response.data : []);
            }
        } catch (err) {
            console.error("Failed to fetch device stock", err);
            setStocks([]); // Reset on error to prevent .map crashes
        }
    }, []);

    const fetchDeviceBaseStock = useCallback(async (deviceId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/machine-base-stock/device/${deviceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBasesStock(response.data);
        } catch (err) {
            console.error("Failed to fetch device base stock", err);
        }
    }, []);

    const fetchInitialData = useCallback(async (initialDeviceId) => {
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // Fetch global summary
            const summaryRes = await axios.get(`${API_BASE_URL}/api/flavors/inventory/summary`, config);
            setSummary(summaryRes.data);

            // Fetch devices
            const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
            setDevices(devicesRes.data);

            if (devicesRes.data.length > 0) {
                const effectiveId = initialDeviceId || devicesRes.data[0].device_id_PK;
                setSelectedDeviceId(effectiveId);
                fetchDeviceStock(effectiveId);
                fetchDeviceBaseStock(effectiveId);
            }

            // Fetch all available flavors for the "Add Inventory" dropdown
            const flavorsRes = await axios.get(`${API_BASE_URL}/api/flavors`, config);
            setFlavors(flavorsRes.data);

            // Fetch all available bases for the "Add Base" dropdown
            const basesRes = await axios.get(`${API_BASE_URL}/api/base`, config);
            setAvailableBases(basesRes.data);

            // Fetch all available load cells
            const loadCellsRes = await axios.get(`${API_BASE_URL}/api/loadcells/get-loadcells`, config);
            if (loadCellsRes.data && loadCellsRes.data.status) {
                setLoadCells(loadCellsRes.data.data);
            }

        } catch (err) {
            console.error("Failed to load initial inventory data", err);
        } finally {
            setLoading(false);
        }
    }, [fetchDeviceStock, fetchDeviceBaseStock]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const deviceParam = queryParams.get("deviceId");
        fetchInitialData(deviceParam);
    }, [fetchInitialData]);

    useEffect(() => {
        if (stocks.length > 0) {
            console.log("Current Machine Stocks Data:", stocks);
        }
    }, [stocks]);


    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        fetchDeviceStock(id);
        fetchDeviceBaseStock(id);
    };

    const handleAddInventory = async () => {
        if (!newInventory.flavor_id) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a flavor', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        if (!newInventory.loadCell_id_FK) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a load cell', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/add-stock`, {
                device_id: String(selectedDeviceId),
                flavor_id: String(newInventory.flavor_id),
                stock: String(newInventory.stock),
                price: String(newInventory.price),
                loadCell_id_FK: String(newInventory.loadCell_id_FK)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsAddModalOpen(false);
            setNewInventory({ flavor_id: "", stock: 0, price: "", loadCell_id_FK: "" });
            fetchDeviceStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Added!', text: 'Inventory has been added successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to add inventory", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleAddBaseInventory = async () => {
        if (!newBaseInventory.base_id) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a base', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-base-stock/add`, {
                device_id: String(selectedDeviceId),
                base_id: String(newBaseInventory.base_id),
                stock: String(newBaseInventory.stock),
                price: String(newBaseInventory.price),
                loadCell_id_FK: String(newBaseInventory.loadCell_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setIsAddBaseModalOpen(false);
            setNewBaseInventory({ base_id: "", stock: 0, price: "", loadCell_id_FK: "" });
            fetchDeviceBaseStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Added!', text: 'Base inventory has been added successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to add base inventory", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditBaseStock = async () => {
        if (!editBaseTarget) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-base-stock/update`, {
                device_id: String(selectedDeviceId),
                base_id: String(editBaseTarget.base_id_FK),
                stock: String(editBaseTarget.stock),
                price: String(editBaseTarget.price),
                loadCell_id_FK: String(editBaseTarget.loadCell_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setIsEditBaseModalOpen(false);
            fetchDeviceBaseStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Base stock has been updated.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update base stock and price", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditStock = async () => {
        if (!editTarget) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/update`, {
                device_id: String(selectedDeviceId),
                flavor_id: String(editTarget.flavor_id_FK),
                stock: String(editTarget.stock),
                price: String(editTarget.price),
                loadCell_id_FK: String(editTarget.loadCell_id_FK)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setIsEditModalOpen(false);
            fetchDeviceStock(selectedDeviceId);
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Stock has been updated successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update stock and price", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditFlavor = async () => {
        if (!editFlavorTarget) return;
        const formData = new FormData();
        formData.append("name", editFlavorTarget.name);
        formData.append("price", editFlavorTarget.price);
        if (editFlavorTarget.imageFile) {
            formData.append("image", editFlavorTarget.imageFile);
        }

        try {
            const token = localStorage.getItem("token");
            await axios.put(`${API_BASE_URL}/api/flavors/${editFlavorTarget.flavors_id_PK}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });
            setIsEditFlavorModalOpen(false);
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Saved!', text: 'Flavor details have been updated.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update flavor", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleToggleActivity = async (item) => {
        const isCurrentlyActive = item.status == 0;
        const actionLabel = isCurrentlyActive ? 'Deactivate' : 'Activate';
        const newStatusLabel = isCurrentlyActive ? 'Inactive' : 'Active';
        const confirmColor = isCurrentlyActive ? '#ef4444' : '#22c55e';

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `${actionLabel} "${item.flavor_name}"? It will be marked as ${newStatusLabel}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: confirmColor,
            cancelButtonColor: '#6b7280',
            confirmButtonText: `Yes, ${actionLabel}!`,
            cancelButtonText: 'Cancel',
            background: '#1b1d1a',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/activity`, {
                device_id_FK: String(selectedDeviceId),
                flavor_id_FK: String(item.flavor_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            fetchDeviceStock(selectedDeviceId);
            Swal.fire({
                icon: 'success',
                title: `${newStatusLabel}!`,
                text: `${item.flavor_name} has been set to ${newStatusLabel}.`,
                background: '#1b1d1a',
                color: '#fff',
                confirmButtonColor: '#22c55e',
                timer: 1800,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || "Failed to toggle activity", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleDeleteFlavorStock = async (item) => {
        Swal.fire({
            title: "Are you sure?",
            text: `You want to delete ${item.flavor_name} from this machine?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete it!",
            background: '#1b1d1a',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("token");
                    await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/delete`, {
                        device_id: String(selectedDeviceId),
                        flavor_id: String(item.flavor_id_FK)
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    fetchDeviceStock(selectedDeviceId);
                    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Flavor stock has been removed.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to delete flavor stock", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
                }
            }
        });
    };

    const handleAddNewFlavor = async () => {
        if (!newFlavor.name || !newFlavor.price || !newFlavor.image) return Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please fill all fields', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        const formData = new FormData();
        formData.append("name", newFlavor.name);
        formData.append("price", newFlavor.price);
        formData.append("image", newFlavor.image);

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/flavors`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });
            setIsNewFlavorModalOpen(false);
            setNewFlavor({ name: "", price: "", image: null });
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Success!', text: 'New flavor added to catalog.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to add new flavor", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const formatImageUrl = (path) => {
        if (!path) return "https://via.placeholder.com/150";
        if (path.startsWith("http")) return path.replace(/\\/g, "/");
        return `${API_BASE_URL}/${path.replace(/\\/g, "/")}`;
    };

    const getStockColor = (stock) => {
        if (stock >= 75) return "text-green-400";
        if (stock >= 25) return "text-yellow-400";
        return "text-red-400";
    };

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center font-bold">Loading Inventory Management...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition flex items-center gap-4">
                    <FiPackage className="text-green-400 text-3xl" />
                    <div>
                        <p className="text-gray-400 text-xs">Total Flavours Tracked</p>
                        <h2 className="text-green-400 text-2xl font-bold">{summary.totalFlavors}</h2>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition flex items-center gap-4">
                    <div className={`${summary.lowStockAlerts > 0 ? "text-red-500 bg-red-500/10" : "text-gray-500 bg-gray-500/10"} p-2 rounded-lg`}>
                        <FiPackage className="text-2xl" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Low Stock Alerts (Global)</p>
                        <h2 className={`${summary.lowStockAlerts > 0 ? 'text-red-500' : 'text-white'} text-2xl font-bold`}>{summary.lowStockAlerts}</h2>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="text-green-400 bg-green-500/10 p-2 rounded-lg">
                            <FiPackage className="text-2xl" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-xs">Avg Daily Fulfillment</p>
                            <h2 className="text-green-400 text-2xl font-bold">{summary.avgDailyUsage}</h2>
                        </div>
                    </div>
                    {userRole === 'superadmin' && (
                        <button
                            onClick={() => setIsNewFlavorModalOpen(true)}
                            className="bg-green-500 hover:bg-green-400 text-black px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition"
                        >
                            <FiPlus /> New Flavour
                        </button>
                    )}
                </div>
            </div>

            {userRole !== 'superadmin' ? (
                <>
                    <div className="mt-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-t border-gray-800 pt-8">
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                Machine Detailed Stock
                            </h2>
                            <p className="text-gray-500 text-xs mt-1">Select a machine to manage its specific flavor inventory</p>
                        </div>

                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <select
                                value={selectedDeviceId || ""}
                                onChange={handleDeviceChange}
                                className="bg-[#1b1d1a] border border-gray-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
                            >
                                {devices.map(device => (
                                    <option key={device.device_id_PK} value={device.device_id_PK}>
                                        {device.device_name} ({device.location})
                                    </option>
                                ))}
                            </select>

                            {(userRole === 'admin' || userRole === 'superadmin') && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setIsAddModalOpen(true)}
                                        className="bg-green-500 hover:bg-green-400 text-black px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition active:scale-95 text-sm"
                                    >
                                        <FiPlus /> Flavour
                                    </button>
                                    <button
                                        onClick={() => setIsAddBaseModalOpen(true)}
                                        className="bg-blue-500 hover:bg-blue-400 text-black px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition active:scale-95 text-sm"
                                    >
                                        <FiPlus /> Base
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Flavor Stocks */}
                    <div className="mt-8">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Powder Flavour Stock</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {stocks.map((item) => (
                                <div key={item.id} className={`bg-[#1b1d1a] rounded-2xl p-6 border shadow-lg transition group ${item.status == 1 ? 'border-red-600/60 shadow-red-900/10' : 'border-gray-700 hover:border-green-500/30'}`}>
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-4">
                                            <div className="h-24 w-24 bg-black rounded-2xl overflow-hidden border border-gray-800 shrink-0">
                                                <img
                                                    src={formatImageUrl(item.image)}
                                                    alt={item.flavor_name}
                                                    className="h-full w-full object-cover"
                                                    onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                                                />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-white">{item.flavor_name}</h3>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-sm bg-green-500/10 text-green-400 px-2 py-0.5 rounded border border-green-500/20 font-bold shadow-[0_0_10px_rgba(34,197,94,0.1)]">
                                                        ₹{item.price && item.price !== "" ? item.price : "0"}
                                                    </span>
                                                    <span className="text-[10px] text-gray-500 uppercase tracking-tighter">per unit</span>
                                                </div>
                                            </div>
                                        </div>

                                        {(userRole === 'admin' || userRole === 'superadmin') && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleToggleActivity(item)}
                                                    className={`p-2 rounded-lg transition ${item.status == 0
                                                        ? 'bg-green-900/20 text-green-400 hover:bg-red-900/20 hover:text-red-400'
                                                        : 'bg-gray-900 text-gray-500 hover:bg-green-900/20 hover:text-green-400'
                                                        }`}
                                                    title={item.status == 0 ? 'Set Inactive' : 'Set Active'}
                                                >
                                                    {item.status == 0
                                                        ? <FiToggleRight size={20} />
                                                        : <FiToggleLeft size={20} />}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditTarget(item);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 bg-gray-900 rounded-lg text-gray-400 hover:text-green-400 hover:bg-green-900/20 transition"
                                                    title="Edit Stock"
                                                >
                                                    <FiEdit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteFlavorStock(item)}
                                                    className="p-2 bg-gray-900 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-900/20 transition"
                                                    title="Delete Stock"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6">
                                        <div className="flex justify-between items-end mb-2">
                                            <span className="text-gray-400 text-xs">Current Stock Level</span>
                                            <span className={`text-xl font-bold ${getStockColor(item.stock)}`}>{item.stock}%</span>
                                        </div>
                                        <div className="w-full h-2 bg-gray-900 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-500 ${item.stock >= 75 ? 'bg-green-400' : item.stock >= 25 ? 'bg-yellow-400' : 'bg-red-400'}`}
                                                style={{ width: `${item.stock}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mt-4">
                                        <div className="flex items-center gap-2 text-green-400 font-black bg-green-500/10 px-3 py-1.5 rounded-lg border border-blue-500/20 w-fit">
                                            <span className="opacity-50 text-[14px]">◈</span>
                                            <span className="uppercase tracking-widest text-[12px]">LOAD CELL: {item.loadCell_name}</span>
                                        </div>
                                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${item.status == 0
                                            ? 'bg-green-500/10 text-green-400 border-green-500/30'
                                            : 'bg-red-500/10 text-red-400 border-red-500/30'
                                            }`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${item.status == 0 ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                                            {item.status == 0 ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>

                                    {/* <div className="mt-4 pt-4 border-t border-gray-800 grid grid-cols-2 gap-y-2 text-[12px] text-gray-500">
                                        <span>Last Updated: {new Date(item.created_at).toLocaleDateString()}</span>
                                        <span className="text-right">Machine: {item.device_id_FK}</span>
                                        {item.loadCell_name && (
                                            <span className="col-span-2 text-green-500/80 font-bold">Associated Load Cell: {item.loadCell_name}</span>
                                        )}
                                    </div> */}
                                </div>
                            ))}

                            {stocks.length === 0 && (
                                <div className="col-span-full py-12 text-center text-gray-600 italic bg-[#1b1d1a] border border-dashed border-gray-700 rounded-2xl">
                                    No flavors assigned to this machine yet. Click "Add Flavour" to categorize inventory.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Base Stocks */}
                    <div className="mt-12">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Liquid Base Stock</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                            {basesStock.map((item) => (
                                <div key={item.machine_base_id_PK} className="bg-[#1b1d1a] rounded-2xl p-5 border border-gray-700 shadow-md hover:border-blue-500/30 transition group">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-16 w-16 bg-black rounded-xl overflow-hidden border border-gray-800 shrink-0">
                                                <img
                                                    src={formatImageUrl(item.image)}
                                                    alt={item.base_name}
                                                    className="h-full w-full object-cover"
                                                    onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                                                />
                                            </div>
                                            <div>
                                                <h3 className="text-md font-bold text-white uppercase">{item.base_name}</h3>
                                                <div className="bg-blue-500/10 text-blue-400 px-2 py-1 rounded text-xs font-bold border border-blue-500/20">
                                                    ₹{item.price}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">

                                            {(userRole === 'admin' || userRole === 'superadmin') && (
                                                <button
                                                    onClick={() => {
                                                        setEditBaseTarget(item);
                                                        setIsEditBaseModalOpen(true);
                                                    }}
                                                    className="p-1.5 bg-gray-900 rounded-lg text-gray-400 hover:text-blue-400 hover:bg-blue-900/20 transition"
                                                >
                                                    <FiEdit2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <div className="flex justify-between items-end mb-1">
                                            <span className="text-gray-500 text-[10px] uppercase">Level</span>
                                            <span className={`text-lg font-bold ${item.stock >= 70 ? 'text-blue-400' : item.stock >= 30 ? 'text-blue-300' : 'text-red-400'}`}>{item.stock}%</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-gray-900 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-500 ${item.stock >= 70 ? 'bg-blue-400' : item.stock >= 30 ? 'bg-blue-500' : 'bg-red-400'}`}
                                                style={{ width: `${item.stock}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-gray-800 text-[10px] text-gray-500 uppercase font-bold flex flex-col gap-2">
                                        <div className="flex justify-between items-center w-full">
                                            <span>Last Sync: {new Date(item.created_at).toLocaleDateString()}</span>
                                            <span className="bg-gray-900 border border-gray-700 px-2 py-0.5 rounded text-[8px] text-gray-400">ID: #{item.machine_base_id_PK}</span>
                                        </div>
                                        {item.loadCell_id_FK && (
                                            <div className="flex items-center gap-2 text-green-400 font-black bg-green-500/10 px-3 py-1.5 rounded-lg border border-blue-500/20 w-fit">
                                                <span className="opacity-50 text-[14px]">◈</span>
                                                <span className="uppercase tracking-widest text-[12px]">LOAD CELL: {item.loadCell_id_FK}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {basesStock.length === 0 && (
                                <div className="col-span-full py-8 text-center text-gray-600 text-sm italic bg-[#1b1d1a] border border-dashed border-gray-700 rounded-2xl">
                                    No base stock assigned (Milk/Water). Click "Add Base" to configure.
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="mt-12 border-t border-gray-800 pt-8">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                        <div>
                            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                <FiPackage className="text-green-500" /> Global Flavour Catalog
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">Manage the master list of flavors across the entire vending network</p>
                        </div>
                        <button
                            onClick={() => setIsNewFlavorModalOpen(true)}
                            className="bg-green-500 hover:bg-green-400 text-black px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition shadow-lg shadow-green-900/20 active:scale-95"
                        >
                            <FiPlus /> Add New Flavour
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {flavors.map((flavor) => (
                            <div key={flavor.flavors_id_PK} className="bg-[#1b1d1a] rounded-3xl overflow-hidden border border-gray-700 hover:border-green-500/50 transition-all duration-300 shadow-lg hover:shadow-2xl flex flex-col group">
                                <div className="h-52 w-full bg-black relative overflow-hidden">
                                    <img
                                        src={formatImageUrl(flavor.image)}
                                        alt={flavor.name}
                                        className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-110 select-none"
                                        onError={(e) => { e.target.src = "https://via.placeholder.com/300x200"; }}
                                    />
                                    <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                                        <div className="bg-black/60 backdrop-blur-xl px-4 py-1.5 rounded-2xl border border-white/10 shadow-xl">
                                            <p className="text-green-400 text-sm font-bold font-mono">₹{flavor.price}</p>
                                        </div>
                                        {userRole === 'superadmin' && (
                                            <button
                                                onClick={() => {
                                                    setEditFlavorTarget({ ...flavor });
                                                    setIsEditFlavorModalOpen(true);
                                                }}
                                                className="bg-black/60 backdrop-blur-xl p-2 rounded-full border border-white/10 text-gray-400 hover:text-green-400 shadow-xl transition"
                                            >
                                                <FiEdit2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#1b1d1a] to-transparent" />
                                </div>

                                <div className="p-6 flex-1 pt-2">
                                    <h3 className="text-xl font-bold text-white group-hover:text-green-400 transition-colors uppercase tracking-tight">{flavor.name}</h3>

                                    <div className="mt-6 space-y-4">
                                        <div>
                                            <div className="flex justify-between items-center text-[10px] text-gray-500 uppercase tracking-widest font-black mb-2">
                                                <span>Global Availability</span>
                                                <span className="text-green-400">{flavor.stock_level}%</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-black rounded-full overflow-hidden border border-gray-800">
                                                <div
                                                    className="h-full bg-green-500 transition-all duration-1000 shadow-[0_0_10px_rgba(34,197,94,0.5)]"
                                                    style={{ width: `${flavor.stock_level}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-2">
                                            <div className="bg-gray-900 px-3 py-1 rounded-lg border border-gray-800">
                                                <p className="text-[10px] text-gray-500 font-mono">ID: #00{flavor.flavors_id_PK}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                                                <span className="text-[10px] text-green-500 font-bold uppercase">Active</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {flavors.length === 0 && (
                        <div className="py-20 text-center bg-[#1b1d1a] rounded-3xl border border-dashed border-gray-700">
                            <FiPackage className="mx-auto text-5xl text-gray-700 mb-4" />
                            <h3 className="text-xl font-bold text-gray-400">Your Catalog is Empty</h3>
                            <p className="text-gray-600 mt-2">Start by adding your first product using the button above.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Add Flavor (Powder) Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsAddModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-6">Assign New Flavour Stock</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Flavour</label>
                                <select
                                    value={newInventory.flavor_id}
                                    onChange={(e) => {
                                        const flavorId = e.target.value;
                                        const selectedFlavor = flavors.find(f => f.flavors_id_PK == flavorId);
                                        setNewInventory({
                                            ...newInventory,
                                            flavor_id: flavorId,
                                            price: selectedFlavor ? selectedFlavor.price : ""
                                        });
                                    }}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="">-- Select Flavor --</option>
                                    {flavors.map(f => (
                                        <option key={f.flavors_id_PK} value={f.flavors_id_PK}>{f.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Initial Stock Level (%)</label>
                                <input
                                    type="number"
                                    min="0" max="100"
                                    value={newInventory.stock}
                                    onChange={(e) => setNewInventory({ ...newInventory, stock: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    placeholder="99"
                                    value={newInventory.price}
                                    onChange={(e) => setNewInventory({ ...newInventory, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={newInventory.loadCell_id_FK}
                                    onChange={(e) => setNewInventory({ ...newInventory, loadCell_id_FK: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleAddInventory}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20"
                            >
                                Assign To Machine
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Base Modal */}
            {isAddBaseModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsAddBaseModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-blue-400 mb-6 font-primary">Add Liquid Base Stock</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Base</label>
                                <select
                                    value={newBaseInventory.base_id}
                                    onChange={(e) => {
                                        const baseId = e.target.value;
                                        const selectedBase = availableBases.find(b => b.base_id_PK == baseId);
                                        setNewBaseInventory({
                                            ...newBaseInventory,
                                            base_id: baseId,
                                            price: selectedBase ? selectedBase.price : ""
                                        });
                                    }}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">-- Select Base --</option>
                                    {availableBases.map(b => (
                                        <option key={b.base_id_PK} value={b.base_id_PK}>{b.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Stock Level (%)</label>
                                <input
                                    type="number"
                                    min="0" max="100"
                                    value={newBaseInventory.stock}
                                    onChange={(e) => setNewBaseInventory({ ...newBaseInventory, stock: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Price (₹)</label>
                                <input
                                    type="number"
                                    value={newBaseInventory.price}
                                    onChange={(e) => setNewBaseInventory({ ...newBaseInventory, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <button
                                onClick={handleAddBaseInventory}
                                className="w-full py-3 bg-blue-500 hover:bg-blue-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-blue-900/20"
                            >
                                Add Base To Machine
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Base Stock Modal */}
            {isEditBaseModalOpen && editBaseTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsEditBaseModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-blue-400 mb-2">Update Base Level</h2>
                        <p className="text-gray-400 text-xs mb-6 uppercase tracking-tight">Refilling {editBaseTarget.base_name}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2 text-center text-blue-400/60">New Level: {editBaseTarget.stock}%</label>
                                <input
                                    type="range"
                                    min="0" max="100"
                                    value={editBaseTarget.stock}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, stock: e.target.value })}
                                    className="w-full accent-blue-500 h-1.5 bg-black rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-gray-600 mt-2 font-bold uppercase">
                                    <span>Empty</span>
                                    <span>Half</span>
                                    <span>Full</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-2">
                                {[20, 50, 80, 100].map(val => (
                                    <button
                                        key={val}
                                        onClick={() => setEditBaseTarget({ ...editBaseTarget, stock: val })}
                                        className="py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-[10px] font-bold hover:bg-blue-500 hover:text-black transition"
                                    >
                                        {val}%
                                    </button>
                                ))}
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Price (₹)</label>
                                <input
                                    type="number"
                                    value={editBaseTarget.price}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={editBaseTarget.loadCell_id_FK || ""}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, loadCell_id_FK: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleEditBaseStock}
                                className="w-full py-3 bg-blue-500 hover:bg-blue-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-blue-900/20"
                            >
                                Confirm Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Stock Modal */}
            {isEditModalOpen && editTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsEditModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-2">Update Stock Level</h2>
                        <p className="text-gray-400 text-xs mb-6 uppercase">Refilling {editTarget.flavor_name}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2 text-center">New Percentage: {editTarget.stock}%</label>
                                <input
                                    type="range"
                                    min="0" max="100"
                                    value={editTarget.stock}
                                    onChange={(e) => setEditTarget({ ...editTarget, stock: e.target.value })}
                                    className="w-full accent-green-500 h-2 bg-black rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-gray-600 mt-2">
                                    <span>0% (Empty)</span>
                                    <span>50%</span>
                                    <span>100% (Full)</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    value={editTarget.price}
                                    onChange={(e) => setEditTarget({ ...editTarget, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={editTarget.loadCell_id_FK || ""}
                                    onChange={(e) => setEditTarget({ ...editTarget, loadCell_id_FK: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-4 gap-2">
                                {[25, 50, 75, 100].map(val => (
                                    <button
                                        key={val}
                                        onClick={() => setEditTarget({ ...editTarget, stock: val })}
                                        className="py-2 bg-gray-900 border border-gray-700 rounded-lg text-xs font-bold hover:bg-green-500 hover:text-black transition"
                                    >
                                        {val}%
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={handleEditStock}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20"
                            >
                                Confirm Refill
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* New Flavor Modal (Master List) */}
            {isNewFlavorModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative">
                        <button
                            onClick={() => setIsNewFlavorModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-6 font-primary">Add Global Flavour</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Coffee"
                                    value={newFlavor.name}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, name: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    placeholder="99"
                                    value={newFlavor.price}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Image</label>
                                <input
                                    type="file"
                                    onChange={(e) => setNewFlavor({ ...newFlavor, image: e.target.files[0] })}
                                    className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                />
                            </div>

                            <button
                                onClick={handleAddNewFlavor}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20"
                            >
                                Register Flavour
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Global Flavor Modal */}
            {isEditFlavorModalOpen && editFlavorTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsEditFlavorModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-6 uppercase tracking-tight font-primary">Edit Flavour Details</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Name</label>
                                <input
                                    type="text"
                                    value={editFlavorTarget.name}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, name: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.price}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Replace Image (Optional)</label>
                                <input
                                    type="file"
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, imageFile: e.target.files[0] })}
                                    className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                />
                            </div>

                            <button
                                onClick={handleEditFlavor}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20 active:scale-95"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InventoryPage;
